import { useEffect, useRef, useState } from 'react';

const BottomBar = () => {
  const [isHidden, setIsHidden] = useState(true);
  const bottomBarRef = useRef(null);
  const logosRef = useRef([]);
  const animationFrameRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const animationStartTimeRef = useRef(null);
  const wasHiddenRef = useRef(true);

  // Scroll handler to show/hide bottom bar - only show at very bottom of page
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Check if we're at the very bottom of the page (within 100px of bottom)
      const isAtBottom = scrollTop + windowHeight >= documentHeight - 100;
      
      setIsHidden(!isAtBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Logo greyscale reveal animation - triggers when bottom bar becomes visible
  useEffect(() => {
    const bottomBar = bottomBarRef.current;
    const logos = logosRef.current.filter(Boolean);
    
    if (!logos.length || !bottomBar) return;

    const revealDuration = 2000; // 2 seconds to fully reveal

    const resetLogos = () => {
      logos.forEach((logo) => {
        if (logo) {
          logo.style.filter = 'grayscale(100%)';
          logo.style.opacity = '0.7';
          logo.classList.remove('revealed');
        }
      });
    };

    const animateReveal = (timestamp) => {
      if (!animationStartTimeRef.current) {
        animationStartTimeRef.current = timestamp;
      }

      const elapsed = timestamp - animationStartTimeRef.current;
      const progress = Math.min(1, elapsed / revealDuration);

      logos.forEach((logo, index) => {
        if (!logo) return;
        
        // Add stagger delay (150ms per logo)
        const staggerDelay = index * 150;
        const adjustedElapsed = Math.max(0, elapsed - staggerDelay);
        const adjustedProgress = Math.min(1, adjustedElapsed / revealDuration);

        // Calculate greyscale and opacity
        const grayscaleAmount = 100 - (adjustedProgress * 100);
        const opacity = 0.7 + (adjustedProgress * 0.3);

        logo.style.filter = `grayscale(${Math.max(0, Math.min(100, grayscaleAmount))}%)`;
        logo.style.opacity = Math.max(0.7, Math.min(1, opacity));

        if (adjustedProgress >= 0.5) {
          logo.classList.add('revealed');
        }
      });

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animateReveal);
      } else {
        isAnimatingRef.current = false;
        animationStartTimeRef.current = null;
      }
    };

    const checkBottomBarVisibility = () => {
      const isCurrentlyHidden = isHidden;

      // Check if bottom bar just became visible (was hidden, now not hidden)
      if (!isCurrentlyHidden && wasHiddenRef.current && !isAnimatingRef.current) {
        // Reset and start animation
        resetLogos();
        isAnimatingRef.current = true;
        animationStartTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateReveal);
      }

      wasHiddenRef.current = isCurrentlyHidden;
    };

    checkBottomBarVisibility();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isHidden]);

  return (
    <div
      id="bottomBar"
      ref={bottomBarRef}
      className={`bottom-bar ${isHidden ? 'hidden' : ''}`}
    >
      {/* Wave SVG decoration */}
      <div className="bottom-bar-wave">
        <svg
          viewBox="0 0 1200 60"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,0 L0,30 Q150,50 300,30 T600,30 T900,30 T1200,30 L1200,0 Z"
            fill="var(--bg-white)"
          />
        </svg>
      </div>

      {/* Logo content */}
      <div className="bottom-bar-content">
        <a
          href="https://voicebroadcasting.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="bottom-bar-logo-link"
        >
          <img
            ref={(el) => (logosRef.current[0] = el)}
            src="/images/footerLogos/VBlogo.png"
            alt="Voice Broadcasting"
            className="bottom-bar-logo vb-logo bigger-logo"
          />
        </a>
        <a
          href="https://pollmakers.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="bottom-bar-logo-link"
        >
          <img
            ref={(el) => (logosRef.current[1] = el)}
            src="/images/footerLogos/pollmakers transparent background.png"
            alt="Pollmakers"
            className="bottom-bar-logo"
          />
        </a>
        <a
          href="https://www.texttosurvey.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="bottom-bar-logo-link"
        >
          <img
            ref={(el) => (logosRef.current[2] = el)}
            src="/images/footerLogos/texttosurvey_logoonly.png"
            alt="Text to Survey"
            className="bottom-bar-logo"
          />
        </a>
        <a
          href="https://townhallmeetings.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="bottom-bar-logo-link"
        >
          <img
            ref={(el) => (logosRef.current[3] = el)}
            src="/images/footerLogos/TownHallLogo.png"
            alt="Townhalls"
            className="bottom-bar-logo townhall-logo"
          />
        </a>
      </div>
    </div>
  );
};

export default BottomBar;
